import { FC } from 'react';
import Chip from '../../chip';
import { Box, Stack } from '@mui/material';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import ActionChip from '@/components/action-chip';
import NoneChip from '@/components/chip/none-chip';
import UserEventSection from './section';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import RiskCard from './risk-card';
import RiskChip from '@/components/chip/risk-chip';
import MetadataCard from './metadata-card';
import AgentLink from '../../agent-link';
import { SensitiveDataEntityConfig } from '@/lib/models/policy-model-v1/sensitive-data';
import ClassifierLink from '../../classifier-link';
import ViolationChip from '@/components/chip/violation-chip';
import UserLink from '@/components/user-link';
import ServiceLink from '@/components/service-link';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { AppDataQueryKey } from '@/lib/query-client';
import { getUserEventPublicService } from '@/lib/services/public-service.service';
import useAppData from '@/hooks/use-app-data.hook';
import { capitalize } from 'lodash';
import { getDomain } from '@/lib/url-helpers';
import { AgentMeta } from '@/lib/models/agent.model';
import { getUserEventAgent } from '@/lib/services/agent.service';
import { IntentScore } from '@/lib/models/user-event';

interface Props {
  data: UserEventModel;
}

const policyFields: ReadonlyField[] = [
  { label: '', prop: 'user' },
  { label: 'Action', prop: 'action' },
  { label: 'Service Name', prop: 'service', rowGroup: 'service' },
  { label: 'Assistant', prop: 'assistant', rowGroup: 'service' },
  { label: 'Model Routing Destination', prop: 'modelRoutingDestination', rowGroup: 'service' },
  { label: 'Service Domain', prop: 'serviceDomain', rowGroup: 'service' },
  { label: 'Violations', prop: 'violations' },
  { label: 'License', prop: 'license' },
];

const getReadonlyData = (
  data: UserEventModel,
  publicService: PublicServiceMeta | null,
  agent: AgentMeta | null
) => {
  const {
    userClaim,
    policyDecision,
    violations,
    firewallDecision,
    fileUpload,
    license,
    requestUrl,
  } = data;

  const policy = {
    user: <UserLink user={userClaim} variant="outlined" />,
    service: <ServiceLink service={publicService} size="medium" />,
    assistant: agent ? <AgentLink agent={agent} canNavigate /> : undefined,
    modelRoutingDestination: firewallDecision?.target?.name || <NoneChip />,
    serviceDomain: getDomain(requestUrl) || <NoneChip />,
    action: <ActionChip action={policyDecision} fileUpload={fileUpload} />,
    violations: violations?.length ? (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {violations.map((violation) => (
          <ViolationChip key={violation} violation={violation} />
        ))}
      </Stack>
    ) : (
      <NoneChip />
    ),
    license: capitalize(license?.detectedLicenseName),
  };

  return { policy };
};

const intentFields: ReadonlyField[] = [
  { label: 'Domains', prop: 'domains', rowGroup: 'domains' },
  { label: 'Actions', prop: 'actions', rowGroup: 'domains' },
  { label: 'Topics', prop: 'topics', rowGroup: 'domains' },
  { label: 'Custom', prop: 'matchedTopics', rowGroup: 'matched' },
];

const getReadonlyIntentData = (data: UserEventModel, matchedTopics: IntentScore[]) => {
  const { domains, actions, topics } = data.intentLabels;
  const hasMatchedTopics = !!matchedTopics.length;

  return {
    domains: domains || <NoneChip notAvailable />,
    actions: actions || <NoneChip notAvailable />,
    topics: topics || <NoneChip notAvailable />,
    matchedTopics: hasMatchedTopics ? (
      <Stack mt={2} gap={1}>
        {matchedTopics.map(({ id, name, confidence }) => {
          return (
            <ClassifierLink key={id} id={id} name={name} type="intent" confidence={confidence} />
          );
        })}
      </Stack>
    ) : (
      <NoneChip />
    ),
  };
};

const sensitiveDataFields: ReadonlyField[] = [
  { label: 'Sensitive Data', prop: 'entities', rowGroup: 'entities' },
  { label: 'Custom', prop: 'customEntities', rowGroup: 'customEntities' },
];

const getReadonlySensitiveDataData = (data: UserEventModel) => {
  const { sensitiveDataTypes, sensitiveDataRegexDetections } = data;
  const hasRegexDetections = !!sensitiveDataRegexDetections?.length;
  const hasSensitiveData = !!sensitiveDataTypes?.length;

  type FrequencyMap = Record<string, number>;
  const sensitiveDataFrequency = sensitiveDataTypes.reduce<FrequencyMap>((acc, type) => {
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return {
    entities: hasSensitiveData ? (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {Object.keys(sensitiveDataFrequency).map((item) => {
          const count = sensitiveDataFrequency[item];
          const type =
            SensitiveDataEntityConfig.find(({ entityType }) => entityType === item)?.name ||
            item.replaceAll('_', ' ');
          const frequency = count > 1 ? `(${count})` : '';
          return <Chip key={item} label={`${String(type)} ${frequency}`} size="small" />;
        })}
      </Stack>
    ) : (
      <NoneChip />
    ),
    customEntities: hasRegexDetections ? (
      <Stack gap={1}>
        {sensitiveDataRegexDetections.map(({ name, classifierId }) => (
          <ClassifierLink key={classifierId} id={classifierId} name={name} type="regex" />
        ))}
      </Stack>
    ) : (
      <NoneChip />
    ),
  };
};

const Overview: FC<Props> = ({ data }) => {
  const psQuery = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data: publicServices = [] } = psQuery;
  const publicService = getUserEventPublicService(data, publicServices);

  const { riskLevel, metadata, riskAssessment, intents, isF5WAIS, license } = data || {};

  const matchedTopics = intents.input.topics.filter(({ id }) => id);

  const agentsQuery = useAppData<AgentMeta[]>(AppDataQueryKey.Agents);
  const { data: agents = [] } = agentsQuery;
  const agent = getUserEventAgent(data, agents);

  const { policy } = getReadonlyData(data, publicService, agent);
  const intentData = getReadonlyIntentData(data, matchedTopics);

  const hasRiskAssessment = !!riskAssessment?.input || !!riskAssessment?.output;
  const hasRiskLevel = !!riskLevel;
  const metadataItemCount = String(Object.keys(metadata || {}).length);
  const hasMetadata = !!metadata;
  const hasLicense = !!license;

  const { isDiscovered = false } = publicService || {};
  const filteredPolicyFields = policyFields
    .filter(({ prop }) => prop !== 'serviceDomain' || isDiscovered)
    .filter(({ prop }) => prop !== 'modelRoutingDestination' || (!isDiscovered && isF5WAIS))
    .filter(({ prop }) => prop !== 'license' || hasLicense);

  return (
    <>
      <UserEventSection>
        <ReadonlyView fields={filteredPolicyFields} data={policy} />
      </UserEventSection>
      <UserEventSection title="Intent Detections">
        <ReadonlyView fields={intentFields} data={intentData} />
      </UserEventSection>
      <UserEventSection title="Entity Detections">
        <ReadonlyView fields={sensitiveDataFields} data={getReadonlySensitiveDataData(data)} />
      </UserEventSection>

      <UserEventSection
        title="Risk Assessment"
        headerActions={hasRiskLevel && <RiskChip value={riskLevel} size="small" />}
      >
        <Box pb={3}>
          {hasRiskAssessment ? <RiskCard userEvent={data} /> : <NoneChip notAvailable />}
        </Box>
      </UserEventSection>

      <UserEventSection
        title="Request Metadata"
        headerActions={<Chip label={metadataItemCount} size="small" color="grey" />}
      >
        <Box pb={3}>{hasMetadata ? <MetadataCard metadata={metadata} /> : <NoneChip />}</Box>
      </UserEventSection>
    </>
  );
};

export default Overview;
